import { FC, useEffect, useRef, useState } from "react"
import { emphasize, styled } from '@mui/material/styles'
import Chip from '@mui/material/Chip'
import { useProductStore } from "../../hooks/useProductStore"
import { Breadcrumbs, Grid, InputAdornment, IconButton, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography, Tooltip } from "@mui/material"
import SearchIcon from '@mui/icons-material/Search'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { withStyles } from '@mui/styles'
import { moneyCL } from "../../toolbox/helpers/money-helpers"
import { useCompanyStore } from "../../hooks/useCompanyStore"
import { company } from "../../types/slices/companyType"
import { useAuthStore } from "../../hooks/useAuthStore"
import { ApiStatus } from "../../types/api/status"
import CloseIcon from '@mui/icons-material/Close'
import { Product } from "../../types/slices/productType"
import { ModalConfirm } from "../../components/common/ModalConfirm/ModalConfirm"


const StyledBreadcrumb = styled(Chip)(({ theme }) => {
    const backgroundColor =
        theme.palette.mode === 'light'
            ? theme.palette.grey[100]
            : theme.palette.grey[800]
    return {
        backgroundColor,
        height: theme.spacing(3),
        color: theme.palette.text.primary,
        fontWeight: theme.typography.fontWeightRegular,
        '&:hover, &:focus': {
            backgroundColor: emphasize(backgroundColor, 0.06),
        },
        '&:active': {
            boxShadow: theme.shadows[1],
            backgroundColor: emphasize(backgroundColor, 0.12),
        },
    }
})

const StyledTableCell = withStyles((theme) => ({
    head: {
        color: theme.palette.common.black,
        backgroundColor: '#69ABC3'
    },
    body: {
        fontSize: 14,
        padding: '4px 8px'
    },
}))(TableCell)

export const ProductCostList: FC = () => {
    const { user } = useAuthStore()
    const { status: statusCompany, getCompanies, editCompany } = useCompanyStore()
    const { status: statusProduct, products, totalRows, getProducts, updateCostMasive } = useProductStore()

    const [dataTable, setDataTable] = useState<Product[]>([])
    const [selectedCompany, setSelectedCompany] = useState<company | null>(null)
    const [percentage, setPercentage] = useState<number>(0)
    const [isEditing, setIsEditing] = useState<boolean>(false)
    const [editTable, setEditTable] = useState<boolean>(false)
    const [showModalConfirm, setShowModalConfirm] = useState<boolean>(false)
    const [modifiedProducts, setModifiedProducts] = useState<{ id: number, cost: number, profit_percentage_product: number }[]>([])


    const [search, setSearch] = useState<string>('')
    const [page, setPage] = useState<number>(1)
    const [perPage, setPerPage] = useState<number>(10)

    const searchRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        getCompanyAPI()
    }, [])

    useEffect(() => {
        if (search === '') {
            getDataAPI()
        } else {
            const timer = setTimeout(() => getDataAPI(), 500)
            return () => clearTimeout(timer)
        }
    }, [page, perPage, search])

    useEffect(() => {
        if (selectedCompany) setPercentage(selectedCompany?.profit_percentage_product || 0)
    }, [selectedCompany])

    const getCompanyAPI = async () => {
        const resp = await getCompanies('', { id: user?.idcompany })
        if (resp[0]?.id) setSelectedCompany(resp[0])
    }

    const getDataAPI = async () => {
        const payload: any = {
            page: page,
            perPage: perPage,
        }
        if (search !== '') payload.search = search

        const resp = await getProducts(payload)
        setDataTable(resp || [])
    }

    const handleChangePage = (event: any, newPage: any) => {
        setPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (event) => {
        setPerPage(+event.target.value)
        setPage(1)
    }

    const onSearch = (search) => {
        setSearch(search)
    }

    const onEditClick = () => {
        setIsEditing(true)
    }

    const onDeleteConfirm = async () => {
        if (!selectedCompany?.id || percentage <= 0) return
        const resp = await editCompany(selectedCompany.id, { profit_percentage_product: percentage })
        if (resp === true) {
            setIsEditing(false)
            setShowModalConfirm(false)
            getDataAPI()
        }
    }

    const handleProductChange = (id: number, field: 'cost' | 'profit_percentage_product' | 'price', value: number) => {
        const updatedTable = dataTable.map((product) => {
            if (product.id === id) {
                if (field === 'profit_percentage_product') {
                    // Si cambia el margen, recalculamos el costo
                    const newCost = product.price / (1 + (value / 100))
                    return {
                        ...product,
                        cost: Math.round(newCost),
                        [field]: value
                    }
                } else if (field === 'price') {
                    // Si cambia el precio, recalculamos el costo
                    const newCost = value / (1 + (product.profit_percentage_product / 100))
                    return {
                        ...product,
                        cost: Math.round(newCost),
                        price: value
                    }
                }
                return {
                    ...product,
                    [field]: value
                }
            }
            return product
        })
        setDataTable(updatedTable)

        const modified = {
            id,
            cost: updatedTable.find(p => p.id === id)?.cost || 0,
            profit_percentage_product: updatedTable.find(p => p.id === id)?.profit_percentage_product || 0,
            price: updatedTable.find(p => p.id === id)?.price || 0
        }

        setModifiedProducts(prevState => {
            const existingIndex = prevState.findIndex(item => item.id === id)
            if (existingIndex >= 0) {
                const updatedState = [...prevState]
                updatedState[existingIndex] = modified
                return updatedState
            } else {
                return [...prevState, modified]
            }
        })
    }

    const onCancel = () => {
        setDataTable(products)
        setModifiedProducts([])
        setEditTable(false)
    }

    const onSave = async () => {
        const resp = await updateCostMasive({ changes: modifiedProducts })
        if (resp === true) {
            setEditTable(false)
            getDataAPI()
        }
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 3 }}>
                    <StyledBreadcrumb
                        label={"Inventario"}
                    />
                    <StyledBreadcrumb
                        label={"Actualización de costos"}
                        onClick={() => {
                            getDataAPI()
                        }}
                    />
                </Breadcrumbs>
            </Grid>
            <Grid item xs={12}
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    marginBottom: '10px'
                }}>
                <Typography variant='h6' component='h1' gutterBottom sx={{ mt: 1, ml: 1 }} className='title-main'>
                    Actualización de costos
                </Typography>
            </Grid>

            <Grid item xs={4}>
                <TextField
                    inputRef={searchRef}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        )
                    }}
                    onChange={(e) => {
                        onSearch(e.target.value)
                        setPage(0)
                    }}
                    placeholder="Buscar..."
                    size="small"
                />
            </Grid>

            <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                    value={percentage}
                    onChange={(e) => setPercentage(parseInt(e.target.value))}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="start">
                                %
                            </InputAdornment>
                        ),
                        readOnly: !isEditing
                    }}
                    size="small"
                    label="Margen"
                    type="number"
                    disabled={!isEditing}
                />
                {isEditing ? (<>
                    <IconButton onClick={() => setShowModalConfirm(true)}>
                        <SaveIcon />
                    </IconButton>
                    <IconButton onClick={() => setIsEditing(false)}>
                        <CloseIcon />
                    </IconButton>
                </>) : (
                    <IconButton onClick={onEditClick}>
                        <EditIcon />
                    </IconButton>
                )}
            </Grid>

            <Grid item xs={4} sx={{ textAlign: 'right' }}>
                {editTable ? (<>
                    <IconButton>
                        <SaveIcon onClick={onSave} />
                    </IconButton>
                    <IconButton onClick={onCancel}>
                        <CloseIcon />
                    </IconButton>
                </>) : (
                    <IconButton onClick={() => setEditTable(true)}>
                        <EditIcon />
                    </IconButton>
                )}
            </Grid>

            <Grid item xs={12}>
                <TableContainer>
                    <Table stickyHeader size="small">
                        <TableHead className="table-header" >
                            <TableRow sx={{ height: '32px' }}>
                                <StyledTableCell className="table-header-text" align="center">
                                    Código
                                </StyledTableCell>
                                <StyledTableCell className="table-header-text" align="center">
                                    Nombre
                                </StyledTableCell>
                                <StyledTableCell className="table-header-text" align="center">
                                    Costo
                                </StyledTableCell>
                                <StyledTableCell className="table-header-text" align="center">
                                    Margen %
                                </StyledTableCell>
                                <StyledTableCell className="table-header-text" align="center">
                                    Precio
                                </StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {statusProduct === ApiStatus.FETCHING ? (
                                <TableRow>
                                    {[0, 1, 2, 3, 4].map((row, i) => (
                                        <TableCell key={i}>
                                            <Skeleton sx={{ height: 20 }} animation="wave" variant="rectangular" />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ) : (
                                dataTable?.map((row, index) => (
                                    <TableRow key={index}>
                                        <TableCell align="center">{row.code_system}</TableCell>
                                        <TableCell align="center">{row.name}</TableCell>
                                        <TableCell align="center">
                                            <TextField
                                                value={editTable ? row.cost : moneyCL(row.cost)}
                                                onChange={(e) => handleProductChange(row.id, 'cost', Number(e.target.value.replace(/\./g, '').replace('$', '').trim()))}
                                                size="small"
                                                variant="standard"
                                                sx={{ width: 150 }}
                                                disabled={!editTable}
                                                onFocus={(e) => e.target.value = row.cost.toString()}
                                                onBlur={(e) => e.target.value = moneyCL(row.cost)}
                                                inputProps={{
                                                    min: 0,
                                                    inputMode: "numeric",
                                                    pattern: "[0-9]*"
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <TextField
                                                value={row.profit_percentage_product}
                                                onChange={(e) => handleProductChange(row.id, 'profit_percentage_product', Number(e.target.value))}
                                                size="small"
                                                variant="standard"
                                                sx={{ width: 150 }}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="start">%</InputAdornment>,
                                                }}
                                                disabled={!editTable}
                                                inputProps={{
                                                    min: 1,
                                                    max: 100,
                                                    inputMode: "numeric",
                                                    pattern: "[0-9]*"
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <TextField
                                                value={editTable ? row.price : moneyCL(row.price)}
                                                onChange={(e) => handleProductChange(row.id, 'price', Number(e.target.value.replace(/\./g, '').replace('$', '').trim()))}
                                                size="small"
                                                variant="standard"
                                                sx={{ width: 150 }}
                                                disabled={!editTable}
                                                onFocus={(e) => e.target.value = row.price.toString()}
                                                onBlur={(e) => e.target.value = moneyCL(row.price)}
                                                inputProps={{
                                                    min: 0,
                                                    inputMode: "numeric",
                                                    pattern: "[0-9]*"
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        rowsPerPageOptions={[5, 10, 25, 100]}
                        count={totalRows}
                        rowsPerPage={perPage}
                        page={(page - 1)}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Filas por página"
                    />
                </TableContainer>
            </Grid>
            {
                showModalConfirm && (
                    <ModalConfirm
                        open={showModalConfirm}
                        closeModal={() => { setShowModalConfirm(false) }}
                        onConfirm={onDeleteConfirm}
                        status={statusCompany === ApiStatus.FETCHING}
                        title="¿Seguro que desea actualizar el margen de utilidad?"
                        text="Esto actualizará el margen de todos sus productos y se aplicará a los productos creados en el futuro."
                    />
                )
            }
        </Grid >
    )
}